<template>
  <div class="h-auto w-full relative">
    <textarea
      v-bind="$attrs"
      v-model="model"
      class="appearance-none px-4 py-3 w-full leading-tight font-body border border-n-100 dark:border-n-700 rounded"
      :class="[
        ...sizeClasses,
        placeholderClasses,
        errorClasses,
        backgroundColors,
        textColors,
        'focus:outline-none focus-within:border-n-200 dark:focus-within:border-n-400',
        disabled ? 'opacity-50 hover:border-transparent pointer-events-none' : ''
      ]"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxlength="maxLength"
      :readonly="readOnly"
    />

    <div class="absolute left-4 bottom-3" v-if="$slots.left">
      <slot name="left"></slot>
    </div>
    <div class="absolute right-4 bottom-3" v-if="$slots.right">
      <slot name="right"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type InputHTMLAttributes } from 'vue'

import { useBackgroundColorsClasses, useTextColorsClasses } from '../classes'
import type { Size } from '../types'

const props = withDefaults(
  defineProps<
    {
      placeholder?: string
      size?: Size
      maxLength?: number
      disabled?: boolean
      readOnly?: boolean
      wrongValue?: boolean
    } & /* @vue-ignore */ InputHTMLAttributes
  >(),
  {
    placeholder: '',
    size: 'small',
    maxLength: 255,
    disabled: false,
    readOnly: false,
    wrongValue: false
  }
)

const model = defineModel<string | null>({ default: null })

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'small':
      return ['text-xs', 'min-h-16']
    case 'medium':
      return ['text-sm', 'min-h-20']
    case 'large':
      return ['text-base', 'min-h-24']
    default:
      return []
  }
})

const placeholderClasses = computed(() => {
  const styles = ['placeholder:text-n-200']
  if (props.size === 'small') {
    styles.push('placeholder:text-xs')
  } else if (props.size === 'medium') {
    styles.push('placeholder:text-sm')
  } else {
    styles.push('placeholder:text-base')
  }
  return styles
})

const errorClasses = computed(() => {
  return props.wrongValue
    ? ['!border-r-500', 'hover:border-r-500', 'focus-within:border-r-500']
    : []
})

const backgroundColors = computed(() => useBackgroundColorsClasses())
const textColors = computed(() => useTextColorsClasses())
</script>
